import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import {Avatar, Button, Divider, Space} from 'antd';
import {ExclamationCircleFilled} from '@ant-design/icons';
import microsoftLogo from '../../../../../static/images/pages/settings/microsoft-logo-sso.png';
import googleLogo from '../../../../../static/images/pages/settings/google-logo-sso.png';
import {serviceStatsActions} from '../../../../../state/actions/serviceStats.actions';
import {
  StyledUsageMonitoring,
  StyledUsageMonitoringService,
  StyledUsageMonitoringServicesList,
  StyledUsageMonitoringConnectedService,
} from './StyledUsageMonitoring';
import SpinSmall from '../../../../SpinSmall';
import {firebaseEvents} from '../../../../../snippets/firebase';
import {elementHelpers, objectHelpers, systemHelpers, textHelpers} from '../../../../../utils/helpers';
import {TeamsOutlinedIcon} from '../../../../../icons';

const logEvent = (eventName, serviceName) => systemHelpers.logEvent(eventName, {name: serviceName});

const UsageMonitoring = ({
  edit = true,
  ...rest
}) => {
  const [trans] = useTranslation(['settings', 'main']);
  const [connectionUrls, setConnectionUrls] = useState({});
  const [connectedServices, setConnectedServices] = useState([]);
  const [storedConnectionId, setStoredConnectionId] = useState(null);
  const [connections, setConnections] = useState({
    data: [],
    loaded: false
  });

  const dispatch = useDispatch();

  const t = (key) => trans(`tabs.usageMonitoring.${key}`);

  const mainT = (key) => trans(`main:${key}`);

  const services = [
    {logo: googleLogo, name: 'google', title: 'Google Workspace', disabled: false},
    {logo: microsoftLogo, name: 'microsoft', title: 'Office 365', disabled: true}
  ];

  useEffect(() => {
    dispatch(serviceStatsActions.getSSCompany(
      (data) => {
        const services = [];
        Object.keys(data).filter(k => k.includes('_connections')).forEach(key => {
          const name = key.split('_')[0];
          data[key].filter(s => s.is_authorized).forEach(service => {
            services.push({
              connected: true,
              id: service.id,
              domain: service.domain,
              name,
            });
          });
        });
        setConnections({loaded: true, data: services});
      },
      () => setConnections({loaded: true, data: []})
    ));
  }, [dispatch]);

  useEffect(() => {
    let connectedServices = [];
    connections.data.forEach(c => {
      const service = services.find(s => s.name === c.name);
      if (service) connectedServices.push({...c, ...service});
    });
    setConnectedServices(connectedServices);
  }, [connections?.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateServices = (serviceId) => {
    setConnections({
      ...connections,
      data: connections.data.filter(s => s.id !== serviceId)
    })
  };

  const openWindowUrl = (url) => window.open(url, '_blank').focus();

  const handleConnect = (serviceName) => {
    const serviceUrl = objectHelpers.getObjProp(connectionUrls, serviceName);
    if (serviceUrl) {
      openWindowUrl(serviceUrl);
    } else {
      dispatch(serviceStatsActions.generateAuthUrl(
        serviceName,
        ({url}) => {
          openWindowUrl(url);
          setConnectionUrls({
            ...connectionUrls,
            [serviceName]: url
          });
          logEvent(firebaseEvents.COMPANY_SETTINGS_CONNECT_SSO, serviceName);
        }
      ));
    }
  }

  const handleDisconnect = (serviceId, serviceName) => {
    elementHelpers.showConfirmModal({
      content: t('disconnectWindow.description'),
      icon: <ExclamationCircleFilled />,
      okType: 'danger',
      onOk: () => {
        setStoredConnectionId(serviceId);
        dispatch(serviceStatsActions.revokeOauth(
          serviceId,
          serviceName,
          (data) => {
            handleUpdateServices(serviceId, data);
            logEvent(firebaseEvents.COMPANY_SETTINGS_DISCONNECT_SSO, serviceName);
          },
          () => setStoredConnectionId(null)
        ));
      },
      title: t('disconnectWindow.title'),
      translate: mainT,
    });
  }

  return (
    <SpinSmall spinning={!connections.loaded}>
      <Space
        direction='vertical'
        size='large'
        style={{width: '100%'}}
        {...rest}
      >
        <StyledUsageMonitoring>
          <Space style={{ marginBottom: 8 }} size='small' className='d-flex-child'>
            <TeamsOutlinedIcon />
            <h3>{t('title')}</h3>
          </Space>

          <p>{t('description')}</p>

          <StyledUsageMonitoringServicesList size='middle'>
            {services.map(({disabled, logo, name, title}) => {
              if (!edit) disabled = true;
              return (
                <StyledUsageMonitoringService
                  key={name}
                  className={disabled && 'disabled'}
                  onClick={() => !disabled && handleConnect(name)}
                >
                  <img src={logo} alt={title} />
                  {title}
                </StyledUsageMonitoringService>
              )
            })}
          </StyledUsageMonitoringServicesList>
          {connectedServices.length > 0 && (
            <>
              <Divider />
              <h3>{t('tabTitle')}</h3>
              {connectedServices.map(({domain, id, name, logo, title}) => (
                <StyledUsageMonitoringConnectedService
                  className={storedConnectionId === id && 'disabled'}
                  key={id}
                >
                  <Space size='small'>
                    <Avatar src={logo}>
                      {textHelpers.getInitials(title)}
                    </Avatar>
                    {domain}
                  </Space>
                  {edit && (
                    <Button
                      onClick={() => handleDisconnect(id, name)}
                      type='text'
                      danger
                    >
                      {mainT('disconnect')}
                    </Button>
                  )}
                </StyledUsageMonitoringConnectedService>
              ))}
            </>
          )}
        </StyledUsageMonitoring>

      </Space>
    </SpinSmall>
  );
}

UsageMonitoring.propTypes = {
  edit: PropTypes.bool,
}

export default UsageMonitoring;
